import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchUpdateKycIdentity } from './service';
import { KYCQueryKeys } from '../../queryKeys';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { AxiosInstance } from 'axios';
import { UpdateKycIdentityInputDto } from '@starsoft/common/dto';

/**
 * Custom hook to update KYC identity information
 *
 * This hook handles updating KYC identity data and managing the mutation state.
 * It uses React Query for data mutation and cache invalidation.
 *
 * @returns {Object} An object containing:
 *   - updateKycIdentity: Function to trigger KYC identity update (non-async)
 *   - updateKycIdentityAsync: Function to trigger KYC identity update (async version)
 *   - isPending: Boolean indicating if mutation is in progress
 */
export function useUpdateKycIdentity() {
  const queryClient: QueryClient = useQueryClient();
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Success callback handler for KYC identity update
   * Invalidates relevant queries to refresh data
   */
  function onSuccess() {
    queryClient.invalidateQueries({ queryKey: [KYCQueryKeys.Me] });
  }

  const { mutate, isPending, mutateAsync } = useMutation({
    mutationFn: (data: UpdateKycIdentityInputDto) =>
      fetchUpdateKycIdentity(data, axiosInstance),
    onSuccess,
  });

  return {
    updateKycIdentity: mutate,
    updateKycIdentityAsync: mutateAsync,
    isPending,
  };
}
